import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, FileText, IndianRupee, User, LogOut, Menu as MenuIcon, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";
import { SiteContentProvider, useSite } from "@/contexts/SiteContent";
import { resolveAssetUrl } from "@/lib/api";

const NAV = [
  { to: "/agent/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/agent/leads", icon: FileText, label: "My leads" },
  { to: "/agent/new-lead", icon: Plus, label: "New lead" },
  { to: "/agent/payouts", icon: IndianRupee, label: "Payouts" },
  { to: "/agent/profile", icon: User, label: "Profile" },
];

function Sidebar({ onLink }) {
  const { content } = useSite();
  const brand = content?.branding || {};
  const logo = resolveAssetUrl(brand.logo_url || "");
  return (
    <>
      <div className="p-5 border-b border-slate-200 flex items-center gap-2.5">
        {logo && <img src={logo} alt="" className="h-9 w-auto"/>}
        <div>
          <div className="font-display font-extrabold text-sm">Agent Portal</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted">TARA Finserv</div>
        </div>
      </div>
      <nav className="p-3 space-y-1">
        {NAV.map(n => (
          <NavLink key={n.to} to={n.to} onClick={onLink}
            data-testid={`agent-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-saffron/15 text-saffron" : "text-ink hover:bg-slate-100"}`}>
            <n.icon className="h-4 w-4" strokeWidth={1.8}/> {n.label}
          </NavLink>
        ))}
      </nav>
    </>
  );
}

function Inner() {
  const { agent, agentLogout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  if (agent === null) return <div className="min-h-screen grid place-items-center text-ink-muted">Checking session…</div>;
  if (!agent) return <Navigate to="/agent/login" replace/>;

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col">
        <Sidebar/>
        <div className="mt-auto p-4 border-t border-slate-200">
          <div className="text-xs text-ink-muted">Logged in as</div>
          <div className="font-mono font-bold text-saffron">{agent.code}</div>
          <div className="text-xs text-ink-muted truncate">{agent.email}</div>
          <Button data-testid="agent-logout" variant="ghost" onClick={async()=>{ await agentLogout(); nav("/agent/login"); }} className="w-full justify-start mt-2 text-ink-muted hover:text-saffron">
            <LogOut className="h-4 w-4 mr-2"/> Logout
          </Button>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={()=>setOpen(false)}/>
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white flex flex-col">
            <Sidebar onLink={() => setOpen(false)}/>
            <Button variant="ghost" onClick={async()=>{ await agentLogout(); nav("/agent/login"); }} className="m-3 justify-start text-ink-muted">
              <LogOut className="h-4 w-4 mr-2"/> Logout
            </Button>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden bg-white border-b border-slate-200 px-4 h-14 flex items-center justify-between">
          <button onClick={()=>setOpen(true)}>{open ? <X className="h-5 w-5"/> : <MenuIcon className="h-5 w-5"/>}</button>
          <div className="font-display font-bold">Agent Portal</div>
          <div className="font-mono text-xs text-saffron">{agent.code}</div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden"><Outlet/></main>
      </div>
    </div>
  );
}

export default function AgentLayout() {
  return <SiteContentProvider><Inner/></SiteContentProvider>;
}
